import { ParticipantProduct, ParticipantGroup, Payment, Notification } from './participant.models';
import { ActivityProduct, EmailType, Participant, PatchOperation } from '@app/_store';

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace ParticipantActions {
  export class Reset {
    static readonly type = '[PARTICIPANT] Reset';
  }

  export class Load {
    static readonly type = '[PARTICIPANT] Load Participant';
  }

  export class DeleteParticipant {
    static readonly type = '[PARTICIPANT] Delete Participant';
    constructor(public participant: Participant) {}
  }

  export class DeleteParticipantGroup {
    static readonly type = '[PARTICIPANT] Delete Participant Group';
    constructor(public groupId: string) {}
  }

  export class UpdateParticipant {
    static readonly type = '[PARTICIPANT] Update Participant';
    constructor(
      public group: string,
      public participantId: string,
      public updates: PatchOperation[]
    ) {}
  }

  export class DeleteParticipantProduct {
    static readonly type = '[PARTICIPANT] Delete Participant Product';
    constructor(
      public participant: Participant,
      public product: ParticipantProduct,
      public forceDelete: boolean
    ) {}
  }

  export class DownloadReceipt {
    static readonly type = '[PARTICIPANT] Download Receipt';
    constructor(public participantId: Participant) {}
  }

  export class AddParticipantsByTag {
    static readonly type = '[PARTICIPANT] Add Participants By Tag';
    constructor(
      public tag: string,
      public includeEntireGroup: boolean
    ) {}
  }

  export class AddParticipantsByMember {
    static readonly type = '[PARTICIPANT] Add Participants By Member';
    constructor(
      public participantId: string,
      public includeEntireGroup: boolean
    ) {}
  }

  export class AddParticipant {
    static readonly type = '[PARTICIPANT] Add Participant';
    constructor(
      public name: string,
      public email: string,
      public mobile: string
    ) {}
  }

  export class LoadNotifications {
    static readonly type = '[PARTICIPANT] Load Notifications';
    constructor(public emailType?: EmailType) {}
  }

  export class LoadGroupNotifications {
    static readonly type = '[PARTICIPANT] Load Group Notifications';
    constructor(public participantGroup: ParticipantGroup) {}
  }

  export class UpdateOrAddNotification {
    static readonly type = '[PARTICIPANT] Update Or Add Notification';
    constructor(
      public group: string,
      public notification: Notification
    ) {}
  }

  export class CalculateTotals {
    static readonly type = '[PARTICIPANT] Calculate Totals';
    constructor(public participantIds: string[]) {}
  }

  export class AddProductToParticipant {
    static readonly type = '[PARTICIPANT] Add Participant Product';
    constructor(
      public participant: Participant,
      public product: ActivityProduct,
      public quantity = 1,
      public comment: string = null,
      public options: { title: string; value: string }[] = [],
      public delivered: Date = null
    ) {}
  }

  export class BatchAddProductToParticipants {
    static readonly type = '[PARTICIPANT] Batch Add Participants Product';
    constructor(
      public participants: Participant[],
      public product: ActivityProduct,
      public quantity = 1
    ) {}
  }

  export class AddCustomProductToParticipant {
    static readonly type = '[PARTICIPANT] Add Custom Participant Product';
    constructor(
      public participant: Participant,
      public title: string,
      public amount: number,
      public quantity: number,
      public comment: string = null,
      public options: { title: string; value: string }[] = [],
      public delivered: Date = null,
      public profit?: number
    ) {}
  }

  export class AddManualPayment {
    static readonly type = '[PARTICIPANT] Add Manual Payment';
    constructor(
      public participant: Participant,
      public date: Date,
      public amount: number,
      public text: string
    ) {}
  }

  export class AddPaymentFromServerEvent {
    static readonly type = '[PARTICIPANT] Add Payment From Server Event';
    constructor(
      public participantId: string,
      public payment: Payment
    ) {}
  }

  export class AddProductFromServerEvent {
    static readonly type = '[PARTICIPANT] Add Product From Server Event';
    constructor(
      public participantId: string,
      public product: ParticipantProduct
    ) {}
  }

  export class RemoveProductFromServerEvent {
    static readonly type = '[PARTICIPANT] Remove Product From Server Event';
    constructor(
      public participantId: string,
      public productId: string
    ) {}
  }

  export class UpdateProductFromServerEvent {
    static readonly type = '[PARTICIPANT] Update Product From Server Event';
    constructor(
      public participantId: string,
      public product: ParticipantProduct
    ) {}
  }

  export class DeletePayment {
    static readonly type = '[PARTICIPANT] Delete Payment';
    constructor(
      public participant: Participant,
      public paymentId: string
    ) {}
  }

  export class MarkPaymentAsVerified {
    static readonly type = '[PARTICIPANT] Mark Payment As Verified';
    constructor(
      public participant: Participant,
      public paymentId: string
    ) {}
  }

  export class UpdatePaymentVerification {
    static readonly type = '[PARTICIPANT] Update Payment Verification';
    constructor(
      public participantId: string,
      public paymentId: string,
      public verified: Date,
      public verifiedBy: string
    ) {}
  }

  export class MarkProductsAsDelivered {
    static readonly type = '[PARTICIPANT] Mark Products As Delivered';
    constructor(
      public participantId: string,
      public items: { participantId: string; productId: string }[]
    ) {}
  }

  export class UpdateDeliveryState {
    static readonly type = '[PARTICIPANT] Update Delivery State';
    constructor(
      public items: { participantId: string; productId: string }[],
      public delivered: Date,
      public deliveredBy: string
    ) {}
  }

  export class UpdateParticipantProduct {
    static readonly type = '[PARTICIPANT] Update Participant Product';
    constructor(
      public participant: Participant,
      public product: ParticipantProduct,
      public quantity: number,
      public comment: string,
      public options: { title: string; value: string }[] = [],
      public delivered: Date,
      public title?: string,
      public amount?: number,
      public profit?: number
    ) {}
  }

  export class SendParticipantActivityLink {
    static readonly type = '[PARTICIPANT] Send Participant Activity Link';
    constructor(
      public participant?: Participant,
      public group?: ParticipantGroup
    ) {}
  }

  export class GetParticipantActivityLinkQRCode {
    static readonly type = '[PARTICIPANT] Get Participant Activity Link QR Code';
    constructor(public participantId: string) {}
  }

  export class UpdateParticipantProductsFromActivityProduct {
    static readonly type = '[PARTICIPANT] Update Participant Products From Activity Product';
    constructor(public activityProduct: ActivityProduct) {}
  }

  export class UpdateIntegrationData {
    static readonly type = '[PARTICIPANT] Update IntegrationData';
    constructor(
      public participantId: string,
      public integrationData: Record<string, unknown>[]
    ) {}
  }
}
